import { BigNumber, ethers } from "ethers";
import { useState } from "react";
import { toast } from "react-toastify";

import { useWeb3React } from "@web3-react/core";

import { ToastRow } from "../components/Row";
import { SalesInfo, SaleType } from "../constants/sale";
import {
  GasEstimateLevels,
  TxnOverride,
  useGasEstimates
} from "../hooks/useGasEstimates";
import { Emoji, Text } from "../theme/components";
import { useErrorMessage } from "./useErrorMessage";
import { useGnarlyGoatsContracts } from "./useGnarlyGoatsContracts";
import { useTokens } from "./usePaginatedTokens";

export function useMintGoats() {
	const { library, account } = useWeb3React()

	const { contract, mintPassContract } = useGnarlyGoatsContracts()

	const { mintPassTokens } = useTokens()

	const { getErrorMessage } = useErrorMessage()

	const [mintAmount, setMintAmount] = useState<number>(1)
	const [mintType, setMintType] = useState<SaleType>(SaleType.ClaimMintPasses)

	const [gasEstimateLevel, setGasEstimateLevel] = useState<GasEstimateLevels>(
		GasEstimateLevels.Market
	)

	const { gasEstimatesMap } = useGasEstimates({
		saleType: mintType,
		mintAmount: mintAmount,
	})

	/**
	 * Mint Gnarly Goat tokens for any sale type
	 * @param amount the number of tokens to mint
	 * @returns
	 */
	async function mint(amount: BigNumber): Promise<boolean> {
		if (!!!contract || !!!mintPassContract || !!!gasEstimatesMap) {
			return false
		}

		let mintTxn: any

		try {
			let txnOverride: TxnOverride = {
				gasPrice: gasEstimatesMap[gasEstimateLevel]?.txnOverride.gasPrice,
			}

			if (SalesInfo[mintType].tokenPriceInEth !== undefined) {
				let tokenPrice = ethers.utils.parseEther(
					String(SalesInfo[mintType].tokenPriceInEth)
				)
				txnOverride.value = tokenPrice.mul(amount)
			}

			switch (mintType) {
				case SaleType.ClaimMintPasses:
					mintTxn = await mintPassContract.claimMintPasses(amount, txnOverride)

					break

				case SaleType.RedeemMintPasses:
					if (mintPassTokens === undefined || mintPassTokens.length === 0) {
						console.log(`No mint pass tokens found for account ${account}`)

						return false
					}

					amount = BigNumber.from(mintPassTokens.length)

					let mintPassTokenIds = mintPassTokens.map((token) => {
						return BigNumber.from(token.tokenId)
					})

					mintTxn = await contract.redeemMintPasses(
						mintPassTokenIds,
						txnOverride
					)

					break

				case SaleType.ClaimWhitelistGoats:
					mintTxn = await contract.claimWhitelistGoats(amount, txnOverride)

					break

				case SaleType.MintGoats:
					mintTxn = await contract.mintGoats(amount, txnOverride)

					break
			}

			validateTxn(mintTxn.hash, amount.toNumber(), library)

			await library.waitForTransaction(mintTxn.hash, 1)

			return true
		} catch (err: any) {
			console.log(err)
			toast.error(
				<ToastRow>
					<Text>Cannot process transaction ({getErrorMessage(err)}). </Text>
					<Emoji>😕</Emoji>
				</ToastRow>
			)

			return false
		}
	}

	return {
		mint,
		gasEstimatesMap,
		setGasEstimateLevel,
		gasEstimateLevel,
		mintType,
		setMintType,
		mintAmount,
		setMintAmount,
	}
}

async function validateTxn(
	txnHash: string,
	mintAmount: number,
	provider: ethers.providers.Web3Provider
) {
	toast.success(
		<ToastRow>
			<Text>Transaction initiated. </Text>
			<Emoji>🙂</Emoji>
		</ToastRow>
	)

	!!provider &&
		(await toast.promise(provider.waitForTransaction(txnHash, 1), {
			pending: {
				render() {
					return (
						<ToastRow>
							<Text>Waiting for confirmation. </Text>
							<Emoji>😎</Emoji>
						</ToastRow>
					)
				},
			},
			success: {
				render() {
					return (
						<ToastRow>
							<Emoji>🐐</Emoji>
							<Text>{` Successfully minted ${mintAmount} token${
								mintAmount === 1 ? '' : 's'
							}! `}</Text>
							<Emoji>🥳</Emoji>
						</ToastRow>
					)
				},
			},
			error: {
				render() {
					return (
						<ToastRow>
							<Emoji>😕</Emoji>
							<Text> Transaction failed.</Text>
						</ToastRow>
					)
				},
			},
		}))
}
